import menuConfig, { menuItemModuleOn } from 'src/config/menu';
import type { MenuItem } from 'src/config/menu';
import {
  passesMenuAccessGate,
  getNavPermissionContext,
  APPOINTMENTS_GROUP,
  COMMISSION_GROUP,
} from 'src/Common/permissions/cloudPosPermissions';
import { BILLING_ONLY_HOME, isBillingRestricted } from 'src/Common/navigation/billingRestriction';
import { getAppointmentFlags, type AppointmentFlags } from 'src/Common/appointments/appointmentFlags';

/** Keep in sync with SidebarContext.normalizeDefaultPOSTerminal (avoid importing SidebarContext → API cycle). */
type DefaultPOSTerminalValue = 'Simple' | 'Terminal3';

function normalizeDefaultPOSTerminal(raw: string | null | undefined): DefaultPOSTerminalValue {
  if (raw === 'Terminal3' || raw === 'Simple') return raw;
  if (raw === 'Touch') return 'Terminal3';
  return 'Simple';
}

function parseUserRoles(): string[] {
  const raw = sessionStorage.getItem('impersonateRoles') ?? localStorage.getItem('userRoles');
  if (!raw) return [];
  return raw.split(',').map((r) => r.trim()).filter(Boolean);
}

/**
 * Default POS mode from session (populated on login via CompanySettings GET).
 * Falls back to Simple — same initial default as SidebarContext before custom-settings API.
 */
function readDefaultPOSTerminalFromSession(): DefaultPOSTerminalValue {
  try {
    const raw = sessionStorage.getItem('companySettings');
    if (!raw) return 'Simple';
    const s = JSON.parse(raw) as Record<string, unknown>;
    const cs = (s.customSettings ?? s.CustomSettings) as Record<string, unknown> | undefined;
    if (cs && typeof cs === 'object') {
      const v = cs.DefaultPOSTerminal ?? cs.defaultPOSTerminal;
      if (v != null && String(v).trim() !== '') return normalizeDefaultPOSTerminal(String(v));
    }
  } catch {
    /* ignore */
  }
  return 'Simple';
}

export function normalizeMenuItemPath(path: string): string {
  const p = path.trim();
  if (p === '/Dashboard' || p.toLowerCase() === '/dashboard') return '/dashboard';
  return p;
}

function itemVisibleLikeSidebar(
  item: MenuItem,
  roles: string[],
  permLookup: Map<string, Set<string>>,
  permBypass: boolean,
  defaultPOSTerminal: DefaultPOSTerminalValue,
  moduleFlags: AppointmentFlags
): boolean {
  // Opt-in module items (Appointments …) only while their switch is on — checked before the access
  // gate, which owners pass unconditionally (the sidebar does the same).
  if (!menuItemModuleOn(item, moduleFlags)) return false;
  // Use the exact same access gate as the sidebar so the post-login redirect and the workspace hub
  // never disagree with the visible menu (a custom role granted Main:Dashboard lands on the dashboard).
  if (!passesMenuAccessGate(item, roles, permLookup, permBypass)) return false;
  if (item.title === 'menu.salesTerminal' && defaultPOSTerminal !== 'Simple') return false;
  if (item.title === 'menu.terminal3' && defaultPOSTerminal !== 'Terminal3') return false;
  return true;
}

const FALLBACK_HOME = '/dashboard';

/** Permission groups of the opt-in modules (Appointments, Commission). */
const MODULE_PERMISSION_GROUPS: ReadonlySet<string> = new Set([APPOINTMENTS_GROUP, COMMISSION_GROUP]);

/** The lookup without the opt-in modules' groups: the permissions as they were before the modules existed. */
function withoutModulePermissionGroups(lookup: Map<string, Set<string>>): Map<string, Set<string>> {
  return new Map(Array.from(lookup.entries()).filter(([group]) => !MODULE_PERMISSION_GROUPS.has(group)));
}

/** Path of the first item (in sidebar order) that passes `visible`, or null. */
function firstVisiblePath(visible: (item: MenuItem) => boolean): string | null {
  for (const group of menuConfig) {
    for (const item of group.items) {
      if (visible(item)) return normalizeMenuItemPath(item.path);
    }
  }
  return null;
}

/**
 * Company opt-in for the Modern Dashboard launcher (custom setting 'ModernDashboard').
 * Read from the same sessionStorage companySettings blob as DefaultPOSTerminal.
 */
export function isModernDashboardEnabled(): boolean {
  try {
    const raw = sessionStorage.getItem('companySettings');
    if (!raw) return false;
    const s = JSON.parse(raw) as Record<string, unknown>;
    const cs = (s.customSettings ?? s.CustomSettings) as Record<string, unknown> | undefined;
    const v = cs?.ModernDashboard ?? cs?.modernDashboard;
    return v != null && String(v).toLowerCase() === 'true';
  } catch {
    return false;
  }
}

/**
 * First sidebar-visible menu path (same ordering and filters as SidebarMenu), for post-login redirect.
 */
export function getPostLoginHomePath(): string {
  if (isBillingRestricted()) {
    return BILLING_ONLY_HOME;
  }
  if (isModernDashboardEnabled()) {
    return '/modern-dashboard';
  }
  const roles = parseUserRoles();
  const { lookup: permLookup, bypass: permBypass } = getNavPermissionContext();
  const defaultPOSTerminal = readDefaultPOSTerminalFromSession();
  const moduleFlags = getAppointmentFlags();

  // 1. The landing page exactly as before the opt-in modules existed: their menu items and their
  //    permission groups are left out, so switching a module on — or a role gaining module keys
  //    (granted in the role editor, or implied by the server) — never moves an existing landing page.
  const legacyLookup = withoutModulePermissionGroups(permLookup);
  const legacyHome = firstVisiblePath(
    (item) =>
      !item.requiresCompanyFlag &&
      itemVisibleLikeSidebar(item, roles, legacyLookup, permBypass, defaultPOSTerminal, moduleFlags)
  );
  if (legacyHome) return legacyHome;

  // 2. Nothing visible that way (a role made only for a module, e.g. a stylist holding Appointments
  //    keys only): the first module item it may open while that module is on; else the usual
  //    fallback — so with every module off the result is always the old one.
  return (
    firstVisiblePath(
      (item) =>
        !!item.requiresCompanyFlag &&
        itemVisibleLikeSidebar(item, roles, permLookup, permBypass, defaultPOSTerminal, moduleFlags)
    ) ?? FALLBACK_HOME
  );
}

/** All sidebar-visible items in menu order (for hub tiles). */
export function collectVisibleMenuItems(): MenuItem[] {
  const roles = parseUserRoles();
  const { lookup: permLookup, bypass: permBypass } = getNavPermissionContext();
  const defaultPOSTerminal = readDefaultPOSTerminalFromSession();
  const moduleFlags = getAppointmentFlags();
  const out: MenuItem[] = [];

  for (const group of menuConfig) {
    for (const item of group.items) {
      if (itemVisibleLikeSidebar(item, roles, permLookup, permBypass, defaultPOSTerminal, moduleFlags)) {
        out.push(item);
      }
    }
  }
  return out;
}

export function hasDashboardNavAccess(): boolean {
  return collectVisibleMenuItems().some((i) => i.title === 'menu.dashboard');
}

/**
 * “Home” in the sidebar shell: analytics dashboard if allowed, otherwise the workspace hub.
 * Use for back buttons, logo — not for post-login (see getPostLoginHomePath).
 */
export function getShellHomePath(): string {
  if (hasDashboardNavAccess()) return '/dashboard';
  return '/home';
}
